import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getPerpInventoryAvgReportData } from "src/api/sp";
import { resetStore } from "src/store/actions";
import { decimal } from "src/utils/genericFunctions";
import {
  PerpInventoryAvgReportData,
  PerpInventoryAvgReportInitialState
} from "./interface";
import { getFormatedDate } from "src/utils/getDates";
import { defaultDateFormat } from "src/config/constants/genericConstants";

export const fetchPerpInventoryAvgReportData = createAsyncThunk(
  "report/fetchPerpInventoryReportData",
  ({ storeId, department }: { storeId: string; department: string }) =>
    getPerpInventoryAvgReportData({
      storeId,
      department
    })
);

const reportDataInitialState: PerpInventoryAvgReportData = {
  date: [],
  inventory: [],
  lyInventory: [],
  varianceVsLW: [],
  sales: [],
  markDown: [],
  markDownPercentage: [],
  throwAway: []
};

const initialState: PerpInventoryAvgReportInitialState = {
  perpInventoryAvgReportData: reportDataInitialState,
  perpInventoryAvgReportGoalData: [],
  perpInventoryAvgInventory: 0,
  perpInventoryAvgVariance: 0,
  perpInventoryAvgLastYear: 0,
  perpInventoryBudget: 0,
  perpInventoryAvgReportDateRange: [],
  perpInventoryAvgReportStatus: "idle",
  perpInventoryAvgReportTrendStartDate: "06/23",
  perpInventoryAvgReportJumpToDate: "",
  productClassOptions: [],
  productSubClassOptions: [],
  department: "",
  classId: [],
  subClassId: []
};

const perpInventoryAvgReportSlice = createSlice({
  name: "perpInventoryAvgReport",
  initialState,
  reducers: {
    setJumpToDate(
      state: PerpInventoryAvgReportInitialState,
      action: {
        payload: string;
      }
    ) {
      state.perpInventoryAvgReportJumpToDate = action.payload;
    },
    setUpdateGoalData(
      state: PerpInventoryAvgReportInitialState,
      action: {
        payload: number[];
      }
    ) {
      state.perpInventoryAvgReportGoalData = action.payload;
    },
    setSelectedDepartment(
      state: PerpInventoryAvgReportInitialState,
      action: {
        payload: string;
      }
    ) {
      state.department = action.payload;
    },
    updateAverageInventory(
      state: PerpInventoryAvgReportInitialState,
      action: {
        payload: any;
      }
    ) {
      state.perpInventoryAvgInventory = action.payload;
    },
    updateAverageLyInventory(
      state: PerpInventoryAvgReportInitialState,
      action: {
        payload: any;
      }
    ) {
      state.perpInventoryAvgLastYear = action.payload;
    },
    updateAverageVariance(
      state: PerpInventoryAvgReportInitialState,
      action: {
        payload: any;
      }
    ) {
      state.perpInventoryAvgVariance = action.payload;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(resetStore, (state, action) => {
        return initialState;
      })
      .addCase(fetchPerpInventoryAvgReportData.pending, (state) => {
        state.perpInventoryAvgReportStatus = "pending";
      })
      .addCase(
        fetchPerpInventoryAvgReportData.fulfilled,
        (state, { payload }) => {
          state.perpInventoryAvgReportGoalData = [];
          const data = payload.data;
          state.perpInventoryAvgReportStatus = "idle";
          state.perpInventoryAvgReportData.date = data.list.map(
            ({
              date,
              isPhysicalInventoryWeek
            }: {
              date: string;
              isPhysicalInventoryWeek: boolean;
            }) => {
              state.perpInventoryAvgReportGoalData.push(data?.goal || 0);
              const dateString = getFormatedDate(date, defaultDateFormat);
              return { date: dateString, isPhysicalInventoryWeek };
            }
          );
          state.perpInventoryAvgReportData.inventory = data.list.map(
            ({ inventory }: { inventory: number }) => inventory
          );
          state.perpInventoryAvgReportData.lyInventory = data.list.map(
            ({ lyInventory }: { lyInventory: number }) => lyInventory
          );
          state.perpInventoryAvgReportData.varianceVsLW = data.list.map(
            ({ varianceVsLW }: { varianceVsLW: number }) => varianceVsLW
          );
          state.perpInventoryAvgReportData.sales = data.list.map(
            ({ sales }: { sales: number }) => sales
          );
          state.perpInventoryAvgReportData.markDown = data.list.map(
            ({ markDown }: { markDown: number }) => markDown
          );
          state.perpInventoryAvgReportData.markDownPercentage = data.list.map(
            ({ markDownPercentage }: { markDownPercentage: number }) =>
              decimal(String(markDownPercentage))
          );
          state.perpInventoryAvgReportData.throwAway = data.list.map(
            ({ throwAway }: { throwAway: number }) => throwAway
          );
          state.perpInventoryBudget = data?.budget;
        }
      )
      .addCase(fetchPerpInventoryAvgReportData.rejected, (state) => {
        state.perpInventoryAvgReportStatus = "error";
        state.perpInventoryAvgReportData = reportDataInitialState;
        state.perpInventoryAvgReportDateRange = [];
        state.perpInventoryAvgReportGoalData = [];
      });
  }
});

export const {
  setJumpToDate,
  setUpdateGoalData,
  setSelectedDepartment,
  updateAverageInventory,
  updateAverageLyInventory,
  updateAverageVariance
} = perpInventoryAvgReportSlice.actions;

export const selectPerpInvReportDepartment = ({
  perpInventoryAvgReport
}: {
  perpInventoryAvgReport: PerpInventoryAvgReportInitialState;
}) => perpInventoryAvgReport.department;

export const selectPerpInventoryAvgReportJumpToDate = ({
  perpInventoryAvgReport
}: {
  perpInventoryAvgReport: PerpInventoryAvgReportInitialState;
}) => perpInventoryAvgReport.perpInventoryAvgReportJumpToDate;

export const selectPerpInventoryAvgReportData = ({
  perpInventoryAvgReport
}: {
  perpInventoryAvgReport: PerpInventoryAvgReportInitialState;
}) => perpInventoryAvgReport.perpInventoryAvgReportData;

export const selectPerpInventoryAvgReportGoalData = ({
  perpInventoryAvgReport
}: {
  perpInventoryAvgReport: PerpInventoryAvgReportInitialState;
}) => perpInventoryAvgReport.perpInventoryAvgReportGoalData;

export const selectPerpInventoryAvgInventory = ({
  perpInventoryAvgReport
}: {
  perpInventoryAvgReport: PerpInventoryAvgReportInitialState;
}) => perpInventoryAvgReport.perpInventoryAvgInventory;

export const selectPerpInventoryAvgLastYear = ({
  perpInventoryAvgReport
}: {
  perpInventoryAvgReport: PerpInventoryAvgReportInitialState;
}) => perpInventoryAvgReport.perpInventoryAvgLastYear;

export const selectPerpInventoryAvgVariance = ({
  perpInventoryAvgReport
}: {
  perpInventoryAvgReport: PerpInventoryAvgReportInitialState;
}) => perpInventoryAvgReport.perpInventoryAvgVariance;

export const selectPerpInventoryBudget = ({
  perpInventoryAvgReport
}: {
  perpInventoryAvgReport: PerpInventoryAvgReportInitialState;
}) => perpInventoryAvgReport.perpInventoryBudget;

export const selectPerpInventoryAvgReportDateRange = ({
  perpInventoryAvgReport
}: {
  perpInventoryAvgReport: PerpInventoryAvgReportInitialState;
}) => perpInventoryAvgReport.perpInventoryAvgReportDateRange;

export const selectPerpInventoryAvgReportListApiStatus = ({
  perpInventoryAvgReport
}: {
  perpInventoryAvgReport: PerpInventoryAvgReportInitialState;
}) => perpInventoryAvgReport.perpInventoryAvgReportStatus;

export default perpInventoryAvgReportSlice.reducer;
